// The page-change navigation, with the player's gesture attached.
//
// The client is more than one page, and the music survives the walk between
// them only if the browser lets the NEXT page start its AudioContext with no
// new press (`audio/handoff.ts`, R320). Chrome grants that when the
// navigation itself carries user activation — its autoplay FAQ calls this
// "contiguous navigations" — and refuses it to a page reached without one.
//
// The chrome acts on POINTERDOWN (ui-map-interface.md §buttons), and that is
// the trap this module exists for: a mouse press grants activation at the
// press, but a TOUCH grants it at the release. A `location.assign` issued
// inside a touch press therefore walks over with no gesture, and the music
// on the next page waits for another tap. So the navigation leaves at once
// where activation is already granted, and otherwise waits for the release
// that grants it. A press that never releases (a cancelled touch) navigates
// on the fallback timer: late is the same as today, and never worse.

import { NAV_RETRY_MS } from '../gen/presentation';

/** How long to wait for the release before navigating without it. */
export const GESTURE_WAIT_MS = 1000;

/** The events that end a press. Each one grants the activation. */
const RELEASES = ['pointerup', 'pointercancel', 'keyup'] as const;

interface HasActivation {
  userActivation?: { isActive: boolean };
}

/** The retry timer of the navigation that left last, or null. */
let retryTimer: ReturnType<typeof setTimeout> | null = null;
let onHide: (() => void) | null = null;

/** Drops the retry of the last navigation. */
function dropRetry(): void {
  if (retryTimer !== null) clearTimeout(retryTimer);
  retryTimer = null;
  if (onHide !== null) window.removeEventListener('pagehide', onHide);
  onHide = null;
}

/**
 * The default navigation: `location.assign` for the first try, and a plain
 * write of `location.href` for the retry, which starts the load again.
 */
function navigate(url: string, retry: boolean): void {
  if (retry) location.href = url;
  else location.assign(url);
}

/**
 * Leaves for `url` now, and once more after `NAV_RETRY_MS` when the page
 * still stands (R1706, ui-map-interface.md §16.12). A navigation whose
 * request waits on a dead HTTP/3 connection never starts, and the button
 * that asked for it looks dead; the second try starts a new request. A
 * `pagehide` says the page is leaving, and drops the retry. Each call
 * drops the retry of the call before it, so a second press navigates at
 * once and is never swallowed.
 */
function leave(url: string, go: (url: string, retry: boolean) => void): void {
  dropRetry();
  const hide = (): void => { dropRetry(); };
  onHide = hide;
  window.addEventListener('pagehide', hide, { once: true });
  retryTimer = setTimeout(() => {
    dropRetry();
    go(url, true);
  }, NAV_RETRY_MS);
  go(url, false);
}

/**
 * Navigates like `location.assign`, deferred to the instant the browser has
 * granted the press's activation. `go` exists for the tests: jsdom cannot
 * navigate. Its second argument is true for the retry of a navigation that
 * did not leave (`leave`).
 */
export function assignWithGesture(url: string,
  go: (url: string, retry: boolean) => void = navigate): void {
  const ua = (navigator as HasActivation).userActivation;
  // Activation already granted (a mouse press, a keydown, or a call shortly
  // after one): leave now. A browser too old to report is treated as not
  // granted — the release path below costs it nothing but the press length.
  if (ua?.isActive) {
    leave(url, go);
    return;
  }
  let done = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  const fire = () => {
    if (done) return;
    done = true;
    if (timer !== null) clearTimeout(timer);
    for (const ev of RELEASES) window.removeEventListener(ev, fire, { capture: true });
    leave(url, go);
  };
  // Capture-phase on window: the button that took the press may be gone by
  // the release, and the release must still be seen.
  for (const ev of RELEASES) window.addEventListener(ev, fire, { capture: true });
  timer = setTimeout(fire, GESTURE_WAIT_MS);
}

/**
 * Reloads the page when the back-forward cache brings it back after it
 * left (R2817, ui-map-interface.md §16.12). A leave of the world stops the
 * frame loop and closes the world socket for good, so the page that the
 * browser BACK button shows again has no socket and no frame. `left`
 * answers true once the page left; a `pageshow` before a leave, or of a
 * fresh load, reloads nothing. `win` and `reload` exist for the tests.
 */
export function reloadOnReturn(left: () => boolean,
  win: Pick<Window, 'addEventListener'> = window,
  reload: () => void = () => location.reload()): void {
  win.addEventListener('pageshow', (ev) => {
    if (ev.persisted && left()) reload();
  });
}
